/**
 * Primitive formatters for the OASIS display model, plus the audience-keyed
 * status vocabularies.
 *
 * WHY THIS EXISTS
 *
 * Two things were duplicated across the two apps and drifting:
 *
 * 1. Date handling. Six components in the internal app each hand-rolled their own
 *    `formatDate`/`formatDateTime` via `toLocaleString`, while the external view
 *    page used Angular's `date` pipe. Both are unsafe for the date-only values the
 *    application body is full of — see {@link formatIsoDate}.
 *
 * 2. Status labels. The internal app has `APP_STATUS_LABELS` /
 *    `COMPONENT_STATUS_LABELS`; the external view page has its own softer map
 *    inline. Those are NOT redundant, they are audience-specific, and collapsing
 *    them would either leak OARM's internal review mechanics to applicants or
 *    make staff screens vague. Both are kept, keyed by audience.
 *
 * Unlike the pair they replace, these maps are total `Record`s over the status
 * unions, so adding a status to the server contract fails to compile until both
 * audiences have been given wording.
 */

import type { ComponentReviewStatus, OasisApplicationStatus } from '../oasis-server.types';
import type { DisplayAudience, DisplaySectionState, DisplayValue } from './oasis-display.types';

// ─── Dates ────────────────────────────────────────────────────────────────────

/**
 * Renders a date-only value EXACTLY AS STORED — `2026-05-15` stays
 * `2026-05-15`.
 *
 * This is a deliberate product decision, not an unfinished one. The application
 * body stores dates as ISO `YYYY-MM-DD` strings, and every attempt to prettify
 * them reintroduces the same bug: `new Date('2026-05-15')` parses as UTC midnight
 * and renders as May 14 in any negative-offset timezone, which is all of the
 * continental US. Angular's `date` pipe does the same thing, because it
 * constructs a `Date` internally. The timeline types already commit to raw ISO
 * "end-to-end (no Date/timezone formatting — deliberate, to avoid UTC/offset
 * drift)", and this keeps the display layer consistent with that.
 *
 * ISO is unambiguous, fixed-width, and sorts lexicographically, which suits
 * reviewers scanning a column of dates better than prose would. The cost is that
 * it reads as slightly technical to an applicant; that was accepted in exchange
 * for never showing anyone the wrong day.
 *
 * DO NOT "improve" this by introducing a `Date`. If localized dates are ever
 * genuinely wanted, they need a timezone-safe formatter that parses the string
 * into parts and never constructs a `Date` from the whole value — and this is the
 * one function that would change.
 *
 * Real instants (`uploadedAt`, `occurredAt`) are a different thing entirely and
 * are safe to format with the `date` pipe. They do not come through here.
 */
export function formatIsoDate(iso: string | null | undefined): string {
	return iso?.trim() ?? '';
}

/**
 * Formats a span as `2024-08-01 – 2027-05-15`.
 *
 * An absent end date, or the legacy `ongoing` flag, reads as "Present" — that
 * word is the one piece of interpretation here, because an empty right-hand side
 * would look like a rendering fault rather than an open-ended entry.
 */
export function formatIsoDateRange(fromIso: string | null | undefined, toIso: string | null | undefined, ongoing = false): string {
	const from = formatIsoDate(fromIso);
	const to = ongoing ? 'Present' : formatIsoDate(toIso);

	if (from && to) return `${from} – ${to}`;
	if (from) return `${from} – Present`;
	return to;
}

// ─── Scalars ──────────────────────────────────────────────────────────────────

/** Duration in whole months, as `1 year 4 months`. Values are already derived. */
export function formatDurationMonths(months: number | null | undefined): string {
	if (months === null || months === undefined || !Number.isFinite(months) || months <= 0) return '';
	const years = Math.floor(months / 12);
	const rest = months % 12;
	const parts: string[] = [];
	if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
	if (rest > 0) parts.push(`${rest} month${rest === 1 ? '' : 's'}`);
	return parts.join(' ');
}

/**
 * Formats a 10-digit US phone number as `(202) 555-0173`, leaving anything else
 * alone. International numbers and extensions are entered free-form, so
 * reformatting them would corrupt what the applicant typed.
 */
export function formatPhone(phone: string | null | undefined): string {
	if (!phone) return '';
	const digits = phone.replace(/\D/g, '');
	if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	return phone;
}

/** Byte count as `1.4 MB`. Used for supporting-document rows. */
export function formatFileSize(bytes: number | null | undefined): string {
	if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '';
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
	const mb = kb / 1024;
	return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** The pieces of a mailing address, in the order they are written. */
export interface AddressParts {
	currentAddress?: string;
	aptSuite?: string;
	city?: string;
	stateProvince?: string;
	otherState?: string;
	country?: string;
	zipPostalCode?: string;
}

/**
 * Renders an address as a single line.
 *
 * `otherState` wins over `stateProvince` when present: the form stores a sentinel
 * in `stateProvince` and the real value in `otherState` for jurisdictions outside
 * the dropdown, so preferring the dropdown value would show the sentinel.
 */
export function formatAddress(parts: AddressParts): string {
	const street = [parts.currentAddress, parts.aptSuite].filter((p) => !!p?.trim()).join(', ');
	const region = parts.otherState?.trim() || parts.stateProvince?.trim() || '';
	const cityRegion = [parts.city?.trim(), region].filter((p) => !!p).join(', ');
	const tail = [cityRegion, parts.zipPostalCode?.trim()].filter((p) => !!p).join(' ');

	return [street, tail, parts.country?.trim()].filter((p) => !!p).join(', ');
}

// ─── Display values ───────────────────────────────────────────────────────────

/** Wording for the two reasons a value can be absent. */
export const ABSENCE_TEXT = {
	not_provided: 'Not provided',
	not_applicable: 'Not applicable',
} as const;

/**
 * Badge wording for a SECTION with nothing behind it.
 *
 * Distinct from {@link ABSENCE_TEXT}, which is field-level. A section can also be
 * `withheld`, which is not an absence at all — it means the data exists and this
 * reader may not see it, so it must not read as "Not provided".
 *
 * Lives here rather than in each host because both view pages render this badge
 * and it is user-facing copy: the whole point of this layer is that the applicant
 * and staff views cannot drift in what they tell a reader. Returns `''` for an
 * answered section, which has no badge.
 */
export function sectionAbsenceLabel(state: DisplaySectionState): string {
	switch (state) {
		case 'withheld':
			return 'Restricted';
		case 'not_provided':
			return ABSENCE_TEXT.not_provided;
		case 'answered':
			return '';
	}
}

/**
 * Flattens a {@link DisplayValue} to a plain string.
 *
 * A convenience for hosts that only need text (a CSV export, a print view, a
 * table cell). A host that wants real markup — paragraphs for long text, a
 * download link for a file, badges for a list — should switch on `kind` itself
 * rather than route everything through here.
 */
export function formatDisplayValue(value: DisplayValue): string {
	switch (value.kind) {
		case 'text':
		case 'longText':
			return value.value;
		case 'date':
			return formatIsoDate(value.iso);
		case 'dateRange':
			return formatIsoDateRange(value.fromIso, value.toIso, value.ongoing);
		case 'list':
			return value.items.join(', ');
		case 'link':
			return value.text;
		case 'file':
			return value.file.filename;
		case 'empty':
			return ABSENCE_TEXT[value.reason];
	}
}

// ─── Status vocabularies, per audience ────────────────────────────────────────

/**
 * Staff wording for the application spine. Copied from the internal app's
 * `APP_STATUS_LABELS`, which it replaces.
 */
export const STAFF_APP_STATUS_LABELS: Record<OasisApplicationStatus, string> = {
	in_progress: 'In Progress',
	submitted: 'Submitted',
	flag_review: 'Flag Review',
	returned: 'Returned',
	resubmitted: 'Resubmitted',
	ineligible: 'Ineligible',
	component_review: 'Component Review',
	ineligible_for_interview: 'Ineligible for Interview',
	ineligible_for_offer: 'Ineligible for Offer',
	offer_accepted: 'Offer Accepted',
	hired: 'Hired',
	not_selected: 'Not Selected',
	withdrawn: 'Withdrawn',
};

/**
 * Applicant wording for the application spine — deliberately softer and less
 * specific than the staff wording.
 *
 * `flag_review` and `component_review` both read "Under Review": which internal
 * queue an application is sitting in is not the applicant's business, and
 * "Flag Review" would read as an accusation. Copied from the external view
 * page's inline map, which it replaces.
 */
export const APPLICANT_APP_STATUS_LABELS: Record<OasisApplicationStatus, string> = {
	in_progress: 'In Progress',
	submitted: 'Submitted',
	flag_review: 'Under Review',
	returned: 'Returned to You',
	resubmitted: 'Resubmitted',
	ineligible: 'Not Eligible',
	component_review: 'Under Review',
	ineligible_for_interview: 'Denied for Interview',
	ineligible_for_offer: 'Denied for Offer',
	offer_accepted: 'Offer Accepted',
	hired: 'Hired',
	not_selected: 'Not Selected',
	withdrawn: 'Withdrawn',
};

/** Staff wording for a component lane. */
export const STAFF_COMPONENT_STATUS_LABELS: Record<ComponentReviewStatus, string> = {
	in_queue: 'In Queue',
	selected_for_interview: 'Awaiting Interview Clearance',
	cleared_for_interview: 'Cleared for Interview',
	not_cleared_for_interview: 'Not Cleared for Interview',
	interview_requested: 'Interview Requested',
	interview_accepted: 'Interview Accepted',
	interview_declined: 'Interview Declined',
	interview_scheduled: 'Interview Scheduled',
	interviewed: 'Interviewed',
	not_selected: 'Not Selected',
	selected_for_offer: 'Awaiting Offer Clearance',
	cleared_for_offer: 'Cleared for Offer',
	not_cleared_for_offer: 'Not Cleared for Offer',
	offer_issued: 'Offer Issued',
	offer_accepted: 'Offer Accepted',
	offer_declined: 'Offer Declined',
	offer_rescinded: 'Offer Rescinded',
};

/**
 * Applicant wording for a component lane.
 *
 * Everything describing DOJ's internal deliberation collapses to "Under Review",
 * including both clearance requests and both refusals. An applicant learns an outcome
 * when it is communicated to them, not by watching a queue advance — and a refused
 * clearance is reversible, so it is not an outcome at all yet.
 *
 * Total rather than partial-with-a-fallback, so a new component status has to be given
 * applicant wording explicitly instead of silently inheriting one. That is what stops
 * internal mechanics leaking.
 */
export const APPLICANT_COMPONENT_STATUS_LABELS: Record<ComponentReviewStatus, string> = {
	in_queue: 'Under Review',
	selected_for_interview: 'Under Review',
	cleared_for_interview: 'Under Review',
	not_cleared_for_interview: 'Under Review',
	interview_requested: 'Interview Requested',
	interview_accepted: 'Interview Accepted',
	interview_declined: 'Interview Declined',
	interview_scheduled: 'Interview Scheduled',
	interviewed: 'Interviewed',
	not_selected: 'Under Review',
	selected_for_offer: 'Under Review',
	cleared_for_offer: 'Under Review',
	not_cleared_for_offer: 'Under Review',
	offer_issued: 'Offer Received',
	offer_accepted: 'Offer Accepted',
	offer_declined: 'Offer Declined',
	offer_rescinded: 'Offer Rescinded',
};

export function appStatusLabel(status: OasisApplicationStatus | undefined, audience: DisplayAudience): string {
	if (!status) return '';
	return audience === 'staff' ? STAFF_APP_STATUS_LABELS[status] : APPLICANT_APP_STATUS_LABELS[status];
}

export function componentStatusLabel(status: ComponentReviewStatus | undefined, audience: DisplayAudience): string {
	if (!status) return '';
	return audience === 'staff' ? STAFF_COMPONENT_STATUS_LABELS[status] : APPLICANT_COMPONENT_STATUS_LABELS[status];
}
